/**
 * Arranging a watch feed for reading, and summarising a subject at a glance.
 *
 * Pure and dependency-free so CRA's jest can test it — the rule this codebase
 * follows because the router v7 that the pages import cannot be loaded under
 * jest at all. Nothing here imports React, the api client, or a type that drags
 * one in.
 *
 * THE FEED IS NOT A LIST IN ARRIVAL ORDER. Somebody opening it wants to know
 * which of the things they watch moved, loudest first, with a day's worth of
 * "they changed a page" collapsed rather than repeated twelve times. The server
 * hands back events newest-first; the grouping and the ranking are here, so the
 * digest email and this screen cannot come to disagree about what "loudest"
 * means (the email's copy of this lives in `jcseo/watch/digest.py`, and both
 * read the same tier order).
 */
import { safeReturnPath, withReturnTo } from '../components/common/returnPath';
import type {
  WatchEvent,
  WatchProbe,
  WatchRunEvent,
  WatchSubject,
  WatchTier,
} from '../types';

/** Loudest first. Mirrors `signals.TIER_ORDER` server-side. */
export const TIER_RANK: Record<WatchTier, number> = {
  IMMEDIATE: 0,
  SAME_DAY: 1,
  DIGEST: 2,
};

export function tierRank(tier: string): number {
  return TIER_RANK[tier as WatchTier] ?? Object.keys(TIER_RANK).length;
}

export interface FeedDay {
  /** `YYYY-MM-DD` in the viewer's own zone, which is the day they mean. */
  day: string;
  label: string;
  events: WatchEvent[];
}

/**
 * Group a page of events into days, loudest first within each day.
 *
 * Days rather than a flat list because "when" is the first thing a reader wants
 * from a monitor, and a date on every row is noise when twenty share one.
 * Grouped in the VIEWER'S zone: an event at 23:40 UTC belongs to the evening the
 * reader had, not to the next morning.
 */
export function groupByDay(events: WatchEvent[], now: Date = new Date()): FeedDay[] {
  const byDay = new Map<string, WatchEvent[]>();
  for (const event of events) {
    const key = dayKey(event.detected_at);
    const bucket = byDay.get(key);
    if (bucket) bucket.push(event);
    else byDay.set(key, [event]);
  }

  return Array.from(byDay.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0))
    .map(([day, rows]) => ({
      day,
      label: dayLabel(day, now),
      events: [...rows].sort(
        (a, b) => tierRank(a.tier) - tierRank(b.tier) || compareDesc(a.detected_at, b.detected_at),
      ),
    }));
}

/** `2026-09-20` for a timestamp, in local time. Empty for a missing one. */
export function dayKey(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function dayLabel(day: string, now: Date = new Date()): string {
  if (!day) return 'Undated';
  if (day === dayKey(now.toISOString())) return 'Today';
  const yesterday = new Date(now.getTime() - 86400000);
  if (day === dayKey(yesterday.toISOString())) return 'Yesterday';
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}

/**
 * The one line a subject card says about its own health.
 *
 * ORDERED BY WHAT IS WRONG, not by what is normal. A reader scanning twelve
 * cards needs the broken one to say so first; "watching 3 pages" is the least
 * interesting thing a card can tell them and so it comes last.
 */
export function subjectStatus(subject: WatchSubject): {
  tone: 'problem' | 'attention' | 'idle' | 'ok';
  text: string;
} {
  const probes = subject.probes ?? [];
  const paused = probes.filter((p) => p.paused_reason);
  if (paused.length) {
    return {
      tone: 'problem',
      text:
        paused.length === 1
          ? `Stopped: ${paused[0].paused_reason}`
          : `${paused.length} checks have stopped working`,
    };
  }

  if (subject.mode === 'DRAFT') {
    return {
      tone: 'attention',
      text: probes.length
        ? 'Not watching yet - go live to start'
        : 'Nothing chosen to watch yet',
    };
  }

  // A subject with NO probes is a different problem from one whose probes are all off, and saying
  // "every check is switched off" when there are no checks sends somebody looking for a toggle
  // that does not exist.
  if (!probes.length) {
    return { tone: 'attention', text: 'Nothing chosen to watch yet' };
  }

  const active = probes.filter((p) => p.is_active);
  if (!active.length) {
    return { tone: 'idle', text: 'Watching nothing - every check is switched off' };
  }

  const unbaselined = active.filter((p) => !p.is_baselined);
  if (unbaselined.length === active.length) {
    // A first capture has nothing to compare against, so it reports nothing. Saying so stops a
    // new subject reading as broken in its first day.
    return { tone: 'attention', text: 'First look not taken yet - nothing to compare against' };
  }

  return {
    tone: 'ok',
    text: `Watching ${active.length} thing${active.length === 1 ? '' : 's'}`,
  };
}

/** "every 6 hours", for a probe's card. Reuses nothing from `cadence.ts` on
 *  purpose: that one speaks about beats and its words are beat-shaped. */
export function intervalLabel(minutes: number | null | undefined): string {
  const n = Number(minutes ?? 0);
  if (!n) return 'never';
  if (n < 60) return `every ${n} min`;
  if (n < 1440) {
    const hours = Math.round(n / 60);
    return `every ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const days = Math.round(n / 1440);
  if (days === 7) return 'weekly';
  return days === 1 ? 'daily' : `every ${days} days`;
}

/** "in about 4 hours" / "due now". Null when there is nothing scheduled. */
export function nextCheckLabel(
  probe: Pick<WatchProbe, 'next_due_at' | 'is_active' | 'paused_reason'>,
  now: Date = new Date(),
): string | null {
  if (!probe.is_active || probe.paused_reason || !probe.next_due_at) return null;
  const due = new Date(probe.next_due_at).getTime();
  if (Number.isNaN(due)) return null;
  const minutes = Math.round((due - now.getTime()) / 60000);
  if (minutes <= 0) return 'due now';
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in about ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `in about ${days} day${days === 1 ? '' : 's'}`;
}

/**
 * Fold a live `watch_alert` into a feed already on screen.
 *
 * IDEMPOTENT ON THE EVENT ID, because the socket and a refetch can both deliver
 * the same event and a feed that appended blindly would show it twice. Returns
 * the same array when there is nothing to add, so React can skip the re-render.
 */
export function mergeAlert(events: WatchEvent[], incoming: WatchEvent): WatchEvent[] {
  if (events.some((e) => e.id === incoming.id)) return events;
  return [incoming, ...events];
}

/** Every alert in a fresh first page that is not shown yet, newest on top. A
 *  check writes several alerts at once, and fetching only the newest one left
 *  the rest out until a reload. Pure. */
export function mergeAlerts(events: WatchEvent[], incoming: WatchEvent[]): WatchEvent[] {
  return incoming.reduceRight((acc, e) => mergeAlert(acc, e), events);
}

function compareDesc(a: string | null, b: string | null): number {
  const left = a ? new Date(a).getTime() : 0;
  const right = b ? new Date(b).getTime() : 0;
  return right - left;
}


/** What a probe card is doing right now, from the run notices that have arrived. */
export interface ProbeActivity {
  probeId: number;
  status: 'queued' | 'started' | 'finished';
  /** "Checking…" while it works; the outcome once it ends. */
  text: string;
  /** True once it has ended, so the card can stop spinning. */
  done: boolean;
  /** Set on a finished notice that wrote alerts, so the caller knows to refetch. */
  found: number;
}

/**
 * Fold a `watch_run` notice into the per-probe activity map.
 *
 * THIS IS WHAT MAKES A QUIET CHECK VISIBLE. The overwhelmingly common outcome of a
 * monitor is "captured, compared, identical", which publishes no alert — so without
 * this the screen would not change at all and pressing Check now would look like
 * pressing a dead button. The notice arrives either way; this turns it into a line.
 *
 * LATER NOTICES WIN, but an out-of-order `started` never overwrites a `finished`:
 * the socket does not promise ordering, and a card that flipped back to "Checking…"
 * after saying "Nothing changed" would be worse than one that never moved.
 */
export function applyRunEvent(
  current: Record<number, ProbeActivity>,
  event: WatchRunEvent,
): Record<number, ProbeActivity> {
  const existing = current[event.probe_id];
  if (existing?.done && event.status !== 'finished') return current;

  const text =
    event.status === 'finished'
      ? event.outcome || 'Finished'
      : event.message || (event.status === 'queued' ? 'Queued…' : 'Checking…');

  return {
    ...current,
    [event.probe_id]: {
      probeId: event.probe_id,
      status: event.status,
      text,
      done: event.status === 'finished',
      found: event.status === 'finished' ? event.events_written : 0,
    },
  };
}

/** Does this notice mean the feed has something new in it? */
export function runEventChangedTheFeed(event: WatchRunEvent): boolean {
  return event.status === 'finished' && event.events_written > 0;
}


/**
 * The link into a watch subject, carrying the way back.
 *
 * ONE BUILDER BECAUSE THE BUG WAS A FORGOTTEN CALL. Two components link into a
 * watch from a boardroom — the button that creates one and the panel that reports
 * on one — and only the first remembered to stamp `?from=`. The symptom was
 * peculiar and hard to attribute: the back button worked the first time (the panel
 * had not rendered yet, so the button's own link was still on screen) and vanished
 * on every later visit. Anything that links into a watch from elsewhere calls this.
 *
 * `from` omitted is legal and means "no trail" — that is the right answer for a
 * link from inside Watch itself, where the breadcrumb already says Watch and a
 * second way out beside it is the clutter `PageHeader` exists to remove.
 */
export function watchSubjectHref(
  subjectId: number,
  from?: string | null,
  label?: string | null,
): string {
  // SANITISED ON THE WAY IN AS WELL AS ON THE WAY OUT. The real guard is on the
  // read side — `useReturnTrail` runs `safeReturnPath` and refuses to render a
  // trail that leaves the site, which is what makes an off-site `from` inert
  // rather than an open redirect. This is belt and braces: every caller passes an
  // in-app path by construction, and a url somebody copies out of the address bar
  // should not carry a value we would refuse to honour anyway.
  return withReturnTo(
    `/watch/subjects/${subjectId}`,
    safeReturnPath(from),
    label ?? undefined,
  );
}

/** One check's alerts on the feed: what it raised, loudest first. */
export interface FeedCheck {
  key: string;
  /** The check that raised them; null for an alert from before runs were kept. */
  runId: number | null;
  events: WatchEvent[];
}

/**
 * A day's alerts, one card per check.
 *
 * ONE CHECK, ONE CARD. Contify's check on Oct 20 raised eighteen alerts, and the
 * feed showed eighteen near-identical cards for one thing that happened. An
 * alert with no check of its own stays a card of one. Loudest check first, then
 * newest, as the day's alerts already are. Pure.
 */
export function groupByCheck(events: WatchEvent[]): FeedCheck[] {
  const out: FeedCheck[] = [];
  const byRun = new Map<number, FeedCheck>();
  for (const e of events) {
    const run = e.run_id ?? null;
    if (run === null) {
      out.push({ key: `event-${e.id}`, runId: null, events: [e] });
      continue;
    }
    const have = byRun.get(run);
    if (have) {
      have.events.push(e);
      continue;
    }
    const fresh: FeedCheck = { key: `run-${run}`, runId: run, events: [e] };
    byRun.set(run, fresh);
    out.push(fresh);
  }
  for (const c of out) {
    c.events.sort((a, b) => tierRank(a.tier) - tierRank(b.tier) || (b.materiality ?? 0) - (a.materiality ?? 0));
  }
  return out;
}
